import { createContext, useContext, useEffect, useState } from "react";
import fr from "./i18n/fr";
import de from "./i18n/de";
import es from "./i18n/es";

export const translations = {
  fr, de, es,
  it: {
    nav: {
      home: "Home", casa: "La Casa", camere: "Camere", gallery: "Gallery",
      dove: "Dove Siamo", cosa: "Cosa Vedere", esperienze: "Esperienze",
      recensioni: "Recensioni", contatti: "Contatti", prenota: "Prenota",
    },
    hero: {
      eyebrow: "Casa Vacanze Margherita · Santa Croce Camerina, Sicilia",
      lines: ["La tua casa in Sicilia,", "tra mare, cultura", "e libertà."],
      sub: "Vivi la Sicilia sud-orientale soggiornando a Casa Vacanze Margherita, il tuo punto di partenza per scoprire il mare, i borghi barocchi e le meraviglie della costa.",
      cta1: "Scopri la Casa",
      cta2: "Verifica disponibilità",
      badges: ["3 camere da letto", "Fino a 6 ospiti", "Wi-Fi veloce", "Aria condizionata", "3 balconi", "Self check-in"],
      scroll: "Scorri per scoprire",
    },
    welcome: {
      eyebrow: "Benvenuti",
      title: "Benvenuti a Casa Vacanze Margherita",
      p1: "Apri la porta, lascia le valigie e inizia a scoprire la Sicilia. Il mare è a pochi minuti, mentre alcuni dei borghi più belli dell'isola ti aspettano poco più avanti.",
      p2: "Un appartamento di 110 m² al secondo piano di una palazzina tranquilla: tre camere da letto, una cucina che profuma di limoni, un soggiorno per le serate lente e tre balconi per respirare l'aria del sud.",
      stats: [{ v: "110", l: "m² di casa" }, { v: "3", l: "camere da letto" }, { v: "6", l: "ospiti max" }, { v: "3", l: "balconi" }],
    },
    comfort: {
      eyebrow: "I Comfort",
      title: "Ogni dettaglio, pensato per la tua vacanza",
      items: [
        { icon: "ChefHat", t: "La cucina dei pranzi lunghi", d: "Attrezzata e luminosa, con tutto il necessario per cucinare insieme i prodotti del mercato — e un balcone a due passi dal tavolo." },
        { icon: "Tv", t: "Sere di film e serie", d: "Due Smart TV con Netflix per le serate in cui vuoi solo stenderti sul divano e ricaricarti per il giorno dopo." },
        { icon: "Snowflake", t: "Il fresco quando serve", d: "Aria condizionata per dormire bene anche nelle notti di agosto, quando fuori la Sicilia non smette di brillare." },
        { icon: "WashingMachine", t: "Valigie più leggere", d: "Lavatrice a disposizione: puoi partire con meno bagagli e restare un giorno in più, se vuoi." },
        { icon: "Armchair", t: "Tre balconi, tre momenti", d: "Il caffè del mattino, il libro del pomeriggio, l'aperitivo al tramonto. Ogni balcone ha la sua ora giusta." },
        { icon: "KeyRound", t: "Arrivi quando vuoi", d: "Self check-in smart: niente orari rigidi, niente attese. Il viaggio è tuo, la casa ti aspetta." },
        { icon: "Car", t: "Parcheggio vicino", d: "Parcheggi comodamente a pochi passi da casa e dimentica l'auto finché non riparte l'esplorazione." },
        { icon: "PawPrint", t: "I tuoi animali sono i benvenuti", d: "Cani e gatti viaggiano con te e con noi sono di casa. Anche loro meritano una vacanza." },
        { icon: "Gift", t: "Un benvenuto che profuma di Sicilia", d: "Ad aspettarti, un piccolo regalo artigianale: il nostro modo di dirti che qui sei di famiglia." },
      ],
    },
    seaCulture: {
      eyebrow: "From Sea to Culture",
      title: "Due mondi, una sola casa",
      motto: "Al mattino il mare. Al pomeriggio un borgo barocco. Alla sera una cena siciliana.",
      sea: { label: "SEA", title: "Il mare a pochi minuti", text: "Spiagge dorate, tramonti sul lungomare e il faro di Punta Secca che veglia sulla casa di Montalbano.", points: ["Marina di Ragusa", "Punta Secca", "Donnalucata"] },
      culture: { label: "CULTURE", title: "Il barocco dietro l'angolo", text: "Pietra dorata, scalinate scenografiche, cioccolato e tradizioni: il Val di Noto è Patrimonio UNESCO.", points: ["Scicli", "Modica", "Ragusa Ibla", "Noto"] },
      cta: "Esplora il territorio",
    },
    roomsTeaser: {
      eyebrow: "Le Camere",
      title: "Tre camere, ognuna con il suo carattere",
      text: "Non camere d'albergo, ma vere stanze di una vera casa: luminose, silenziose, pronte per il tuo riposo.",
      cta: "Scopri le camere",
    },
    rooms: {
      matrimoniale: { name: "Camera Matrimoniale", beds: "Letto matrimoniale · 2 posti letto", d: "Ampia e luminosa, con balcone, grande armadio a specchio e il silenzio giusto per dormire davvero.", features: ["Balcone privato", "Aria condizionata", "Armadio a specchio"] },
      singola: { name: "Camera Singola", beds: "Letto singolo · 1 posto letto", d: "Un rifugio tranquillo e raccolto, perfetto per chi viaggia con la famiglia ma ama il proprio spazio.", features: ["Luminosa e silenziosa", "Ideale per ragazzi"] },
      castello: { name: "Camera con Letto a Castello", beds: "Letto a castello · 2 posti letto", d: "I bambini la adorano, gli amici la apprezzano: due posti letto, aria condizionata e balcone.", features: ["Balcone", "Aria condizionata", "Perfetta per i più piccoli"] },
    },
    position: {
      eyebrow: "La Posizione",
      title: "Tra il mare e la Sicilia che vuoi scoprire.",
      text: "Casa Vacanze Margherita è a Santa Croce Camerina: in pochi minuti raggiungi il mare e da qui ogni giornata può avere una direzione diversa — la costa, i borghi, il barocco.",
      note: "Tempi di percorrenza indicativi in auto",
      min: "min circa",
      here: "Sei qui",
      cta: "Vedi dove siamo",
    },
    destinations: {
      santacroce: { name: "Santa Croce Camerina", d: "Il tuo quartier generale: un borgo autentico, con mercati, forni e la Sicilia di tutti i giorni." },
      puntasecca: { name: "Punta Secca", d: "La casa del Commissario Montalbano, il faro e una spiaggia che sembra un set cinematografico." },
      marina: { name: "Marina di Ragusa", d: "Lungomare, porto turistico, ristoranti sul mare e spiagge attrezzate per tutta la famiglia." },
      donnalucata: { name: "Donnalucata", d: "Un borgo di pescatori con le barche colorate e il mercato del pesce al mattino." },
      scicli: { name: "Scicli", d: "Un salotto barocco incastonato tra le cave: palazzi, chiese e la via più fotografata del sud-est." },
      modica: { name: "Modica", d: "Il Duomo di San Giorgio e il cioccolato più antico d'Italia, da gustare dove è nato." },
      ragusa: { name: "Ragusa Ibla", d: "Un presepe di pietra dorata: vicoli, giardini e il Duomo che domina la valle." },
      noto: { name: "Noto", d: "La capitale del barocco siciliano: un corso di palazzi dorati che al tramonto si accende." },
    },
    experiences: {
      eyebrow: "Esperienze",
      title: "Non soggiornare soltanto. Vivi la Sicilia.",
      text: "Ogni giornata può essere diversa: ti aiutiamo noi a scegliere, con i consigli di chi questa terra la vive.",
      more: "Scopri tutte le esperienze",
      items: [
        { id: "mare", t: "Mare", d: "Spiagge libere e attrezzate, calette e tramonti sul lungomare." },
        { id: "barocco", t: "Barocco", d: "Scicli, Modica, Ragusa Ibla, Noto: la Sicilia Patrimonio UNESCO." },
        { id: "enogastronomia", t: "Enogastronomia", d: "Cioccolato di Modica, granita a colazione, pesce fresco a cena." },
        { id: "borghi", t: "Borghi", d: "Punta Secca, Donnalucata e i paesi dove il tempo rallenta." },
        { id: "famiglia", t: "Famiglia", d: "Spiagge sicure, spazi veri, giochi in casa: bambini felici, genitori rilassati." },
        { id: "natura", t: "Natura", d: "Campagne di ulivi e carrubi, muretti a secco e riserve sul mare." },
        { id: "relax", t: "Relax", d: "Il balcone, un libro, il sole: la vacanza più semplice che esista." },
        { id: "cultura", t: "Cultura", d: "Ceramiche, feste di paese, musei e le location di Montalbano." },
        { id: "escursioni", t: "Escursioni", d: "Trekking costieri, cavallate e giornate in barca lungo la costa." },
      ],
    },
    audience: {
      eyebrow: "Per Chi è Perfetta",
      title: "Riconosciti nella tua vacanza",
      items: [
        { id: "coppie", t: "Coppie", d: "Per una fuga romantica in Sicilia, tra tramonti sul mare e cene nei borghi." },
        { id: "famiglie", t: "Famiglie", d: "Lo spazio e la libertà di sentirsi a casa: giochi, cucina e spiagge sicure." },
        { id: "gruppi", t: "Gruppi di amici", d: "Tre camere e tutti gli spazi per vivere la vacanza insieme." },
        { id: "viaggiatori", t: "Viaggiatori", d: "La base ideale per esplorare il sud-est della Sicilia, ogni giorno in una direzione nuova." },
      ],
    },
    reviews: {
      eyebrow: "Recensioni",
      title: "Le parole dei nostri ospiti",
      scoreWord: "Eccezionale",
      countText: "Basato su 10 recensioni verificate",
      verified: "Ospite verificato",
      reviewsWord: "recensioni",
      mapsLink: "Vedi tutte su Google Maps",
      readAll: "Leggi tutte le recensioni su Booking.com",
      cats: { cleanliness: "Pulizia", comfort: "Comfort", value: "Qualità/prezzo", staff: "Staff", facilities: "Servizi", location: "Posizione" },
      cta: "Tutte le recensioni",
    },
    direct: {
      eyebrow: "Prenotazione Diretta",
      title: "Prenotando direttamente con noi",
      items: [
        { icon: "MessagesSquare", t: "Assistenza diretta", d: "Parli con chi ti accoglierà, senza intermediari." },
        { icon: "Smartphone", t: "Comunicazione semplice", d: "Un messaggio su WhatsApp e hai la risposta che cerchi." },
        { icon: "MapPinned", t: "Consigli personalizzati", d: "Spiagge, ristoranti e borghi: ti diciamo quelli che amiamo noi." },
        { icon: "CalendarCheck", t: "Massima flessibilità", d: "Date, orari di arrivo, esigenze particolari: ne parliamo insieme." },
      ],
      cta: "Richiedi un preventivo",
    },
    finalCta: {
      title: "La Sicilia ti aspetta.",
      text: "Porta con te la voglia di scoprire. Al resto penserà Casa Vacanze Margherita.",
      cta: "Verifica disponibilità",
    },
    footer: {
      tagline: "La Sicilia ti aspetta.",
      explore: "Esplora",
      contacts: "Contatti",
      rights: "Tutti i diritti riservati.",
      payoff: "From Sea to Culture",
    },
    form: {
      title: "Verifica disponibilità",
      subtitle: "Compila il modulo: ti risponderemo al più presto con disponibilità e preventivo.",
      name: "Nome e cognome",
      email: "Email",
      phone: "Telefono (facoltativo)",
      checkin: "Check-in",
      checkout: "Check-out",
      guests: "Ospiti",
      pets: "Viaggio con animali",
      message: "Messaggio",
      messagePh: "Raccontaci qualcosa del tuo viaggio...",
      submit: "Invia richiesta",
      sending: "Invio in corso...",
      success: "Richiesta inviata! Ti risponderemo al più presto.",
      error: "Qualcosa è andato storto. Riprova o scrivici su WhatsApp.",
      whatsapp: "Continua su WhatsApp",
      waMsg: "Ciao, vorrei ricevere informazioni su Casa Vacanze Margherita per un soggiorno",
    },
    contact: {
      title: "Contatti",
      heroTitle: "Hai bisogno di informazioni?",
      heroSub: "Scrivici direttamente: rispondiamo personalmente a ogni messaggio.",
      whatsapp: "WhatsApp",
      phone: "Telefono",
      email: "Email",
      where: "Dove siamo",
      formTitle: "Scrivici",
      send: "Invia messaggio",
      success: "Messaggio inviato! Ti risponderemo presto.",
    },
    pages: {
      casa: { eyebrow: "La Casa", title: "Una vera casa siciliana, pronta ad accoglierti", seo: "La Casa | Casa Vacanze Margherita", desc: "Scopri l'appartamento di 110 m²: 3 camere, cucina attrezzata, soggiorno con Smart TV, 3 balconi e tutti i comfort per la tua vacanza in Sicilia." },
      camere: { eyebrow: "Le Camere", title: "Stanze vere, per riposi veri", seo: "Le Camere | Casa Vacanze Margherita", desc: "Tre camere da letto luminose fino a 6 ospiti: matrimoniale, singola e con letto a castello. Casa vacanze a Santa Croce Camerina, Ragusa." },
      gallery: { eyebrow: "Gallery", title: "Guarda la casa, immagina la vacanza", seo: "Gallery | Casa Vacanze Margherita", desc: "Le fotografie reali di Casa Vacanze Margherita: camere, cucina, spazi, dettagli e il territorio della Sicilia sud-orientale." },
      dove: { eyebrow: "Dove Siamo", title: "Tra il mare e la Sicilia che vuoi scoprire.", seo: "Dove Siamo | Casa Vacanze Margherita — Santa Croce Camerina", desc: "Casa Vacanze Margherita è a Santa Croce Camerina (RG): a pochi minuti da Marina di Ragusa e Punta Secca, base ideale per Scicli, Modica, Ragusa Ibla e Noto." },
      cosa: { eyebrow: "Cosa Vedere", title: "Il sud-est della Sicilia, giorno per giorno", seo: "Cosa Vedere | Sicilia Sud-Orientale | Casa Vacanze Margherita", desc: "Marina di Ragusa, Punta Secca, Scicli, Modica, Ragusa Ibla, Noto: cosa vedere vicino a Casa Vacanze Margherita, con tempi di percorrenza." },
      esperienze: { eyebrow: "Esperienze", title: "Non soggiornare soltanto. Vivi la Sicilia.", seo: "Esperienze in Sicilia | Casa Vacanze Margherita", desc: "Mare, barocco, enogastronomia, borghi e natura: le esperienze da vivere durante il tuo soggiorno a Casa Vacanze Margherita." },
      recensioni: { eyebrow: "Recensioni", title: "Le parole dei nostri ospiti", seo: "Recensioni | Casa Vacanze Margherita", desc: "Cosa dicono gli ospiti di Casa Vacanze Margherita, casa vacanze a Santa Croce Camerina vicino Marina di Ragusa." },
      prenota: { eyebrow: "Prenota", title: "Prenota il tuo soggiorno", seo: "Prenota | Casa Vacanze Margherita", desc: "Verifica la disponibilità di Casa Vacanze Margherita e prenota direttamente il tuo soggiorno in Sicilia." },
    },
    casaPage: {
      p1: "Al secondo piano di una palazzina tranquilla nel cuore di Santa Croce Camerina, Casa Vacanze Margherita è un appartamento di 110 m² pensato per chi cerca una casa vera, non una stanza d'albergo.",
      p2: "Lo stile è moderno con tocchi vintage e dettagli siciliani: la tovaglia a limoni, le piastrelle decorate, la mandala disegnata a mano. Ogni angolo racconta la cura di chi questa casa la ama.",
      petsTitle: "Anche i tuoi amici a quattro zampe",
      petsText: "Gli animali sono sempre i benvenuti: portali con te, qui troveranno spazio e coccole.",
      giftTitle: "Il benvenuto è di casa",
      giftText: "All'arrivo troverai un piccolo regalo artigianale che profuma di Sicilia: è il nostro modo di darti il benvenuto.",
      faqTitle: "Domande frequenti",
      faq: [
        { q: "Come posso prenotare?", a: "Direttamente dal sito con il modulo di richiesta, oppure scrivendoci su WhatsApp, per email o telefono. Ti risponderemo con disponibilità e preventivo." },
        { q: "Quante persone ospita la casa?", a: "L'appartamento ospita comodamente fino a 6 persone: è perfetto per famiglie, coppie e gruppi di amici." },
        { q: "Gli animali sono ammessi?", a: "Sì, i tuoi amici a quattro zampe sono sempre i benvenuti." },
        { q: "Come funziona il check-in?", a: "Con il self check-in smart arrivi all'ora che preferisci, senza vincoli di orario." },
      ],
    },
    dovePage: {
      howTitle: "Come arrivare",
      car: { t: "In auto", d: "Da Catania segui la A18 verso Ragusa e prosegui per Santa Croce Camerina (circa 1h45). Dall'aeroporto di Comiso la casa dista circa 30 minuti." },
      plane: { t: "In aereo", d: "Gli aeroporti più vicini sono Comiso (CIY) e Catania (CTA). Dall'aeroporto puoi noleggiare un'auto o prenotare un transfer privato." },
      bus: { t: "In bus", d: "Da Ragusa e Comiso partono bus diretti per Santa Croce Camerina, con fermata a pochi passi dalla casa." },
      quiet: "La casa si trova a pochi metri dal centro storico, in una zona tranquilla e servita.",
    },
  },
  en: {
    nav: {
      home: "Home", casa: "The House", camere: "Rooms", gallery: "Gallery",
      dove: "Location", cosa: "What to See", esperienze: "Experiences",
      recensioni: "Reviews", contatti: "Contact", prenota: "Book Now",
    },
    hero: {
      eyebrow: "Casa Vacanze Margherita · Santa Croce Camerina, Sicily",
      lines: ["Your home in Sicily,", "between sea, culture", "and freedom."],
      sub: "Experience South-Eastern Sicily staying at Casa Vacanze Margherita — your perfect base to discover the sea, the baroque towns and the wonders of the coast.",
      cta1: "Discover the House",
      cta2: "Check availability",
      badges: ["3 bedrooms", "Sleeps up to 6", "Fast Wi-Fi", "Air conditioning", "3 balconies", "Self check-in"],
      scroll: "Scroll to explore",
    },
    welcome: {
      eyebrow: "Welcome",
      title: "Welcome to Casa Vacanze Margherita",
      p1: "Open the door, drop your bags and start discovering Sicily. The sea is just minutes away, and some of the island's most beautiful baroque towns wait just a little further down the road.",
      p2: "A 110 sqm apartment on the second floor of a quiet building: three bedrooms, a kitchen scented with lemons, a living room for slow evenings and three balconies to breathe in the southern air.",
      stats: [{ v: "110", l: "sqm of home" }, { v: "3", l: "bedrooms" }, { v: "6", l: "max guests" }, { v: "3", l: "balconies" }],
    },
    comfort: {
      eyebrow: "Amenities",
      title: "Every detail, designed for your holiday",
      items: [
        { icon: "ChefHat", t: "A kitchen for long lunches", d: "Fully equipped and bright, with everything you need to cook market-fresh ingredients together — and a balcony just steps from the table." },
        { icon: "Tv", t: "Movie and series nights", d: "Two Smart TVs with Netflix for the evenings when you just want to stretch out on the sofa and recharge for tomorrow." },
        { icon: "Snowflake", t: "Cool air when you need it", d: "Air conditioning for a good night's sleep, even on those August nights when Sicily never stops shining." },
        { icon: "WashingMachine", t: "Pack lighter", d: "A washing machine at your disposal: bring less luggage and stay one day longer, if you like." },
        { icon: "Armchair", t: "Three balconies, three moments", d: "Morning coffee, an afternoon book, an aperitivo at sunset. Every balcony has its perfect hour." },
        { icon: "KeyRound", t: "Arrive whenever you like", d: "Smart self check-in: no rigid schedules, no waiting. The journey is yours, the house is waiting." },
        { icon: "Car", t: "Parking nearby", d: "Park comfortably a few steps from the house and forget the car until the next exploration." },
        { icon: "PawPrint", t: "Your pets are welcome", d: "Dogs and cats travel with you and feel at home with us. They deserve a holiday too." },
        { icon: "Gift", t: "A welcome that smells of Sicily", d: "A small handcrafted gift awaits you: our way of saying that here, you're family." },
      ],
    },
    seaCulture: {
      eyebrow: "From Sea to Culture",
      title: "Two worlds, one home",
      motto: "The sea in the morning. A baroque town in the afternoon. A Sicilian dinner at night.",
      sea: { label: "SEA", title: "The sea, minutes away", text: "Golden beaches, sunsets on the promenade and the Punta Secca lighthouse watching over Inspector Montalbano's house.", points: ["Marina di Ragusa", "Punta Secca", "Donnalucata"] },
      culture: { label: "CULTURE", title: "Baroque around the corner", text: "Golden stone, scenic staircases, chocolate and traditions: the Val di Noto is a UNESCO World Heritage Site.", points: ["Scicli", "Modica", "Ragusa Ibla", "Noto"] },
      cta: "Explore the area",
    },
    roomsTeaser: {
      eyebrow: "The Rooms",
      title: "Three rooms, each with its own character",
      text: "Not hotel rooms, but real rooms in a real home: bright, quiet, ready for your rest.",
      cta: "Discover the rooms",
    },
    rooms: {
      matrimoniale: { name: "Double Room", beds: "Double bed · sleeps 2", d: "Spacious and bright, with a balcony, a large mirrored wardrobe and the right kind of silence for real rest.", features: ["Private balcony", "Air conditioning", "Mirrored wardrobe"] },
      singola: { name: "Single Room", beds: "Single bed · sleeps 1", d: "A quiet, cosy retreat, perfect for those travelling with family who still love their own space.", features: ["Bright and quiet", "Ideal for teenagers"] },
      castello: { name: "Bunk Bed Room", beds: "Bunk bed · sleeps 2", d: "Children love it, friends appreciate it: two beds, air conditioning and a balcony.", features: ["Balcony", "Air conditioning", "Perfect for the little ones"] },
    },
    position: {
      eyebrow: "The Location",
      title: "Between the sea and the Sicily you want to discover.",
      text: "Casa Vacanze Margherita is in Santa Croce Camerina: reach the sea in minutes, and from here every day can take a different direction — the coast, the villages, the baroque.",
      note: "Approximate driving times",
      min: "min approx.",
      here: "You are here",
      cta: "See where we are",
    },
    destinations: {
      santacroce: { name: "Santa Croce Camerina", d: "Your home base: an authentic village with markets, bakeries and everyday Sicily." },
      puntasecca: { name: "Punta Secca", d: "Inspector Montalbano's house, the lighthouse and a beach that feels like a film set." },
      marina: { name: "Marina di Ragusa", d: "Promenade, marina, seafront restaurants and equipped beaches for the whole family." },
      donnalucata: { name: "Donnalucata", d: "A fishing village with colourful boats and a morning fish market." },
      scicli: { name: "Scicli", d: "A baroque parlour set between quarries: palaces, churches and the most photographed street in the south-east." },
      modica: { name: "Modica", d: "The San Giorgio Cathedral and Italy's oldest chocolate, tasted where it was born." },
      ragusa: { name: "Ragusa Ibla", d: "A golden-stone nativity scene: alleys, gardens and a Cathedral towering over the valley." },
      noto: { name: "Noto", d: "The capital of Sicilian baroque: a boulevard of golden palaces that glows at sunset." },
    },
    experiences: {
      eyebrow: "Experiences",
      title: "Don't just stay. Live Sicily.",
      text: "Every day can be different: we'll help you choose, with tips from people who truly live this land.",
      more: "Discover all experiences",
      items: [
        { id: "mare", t: "Sea", d: "Free and equipped beaches, coves and sunsets on the promenade." },
        { id: "barocco", t: "Baroque", d: "Scicli, Modica, Ragusa Ibla, Noto: UNESCO-listed Sicily." },
        { id: "enogastronomia", t: "Food & Wine", d: "Modica chocolate, granita for breakfast, fresh fish for dinner." },
        { id: "borghi", t: "Villages", d: "Punta Secca, Donnalucata and the towns where time slows down." },
        { id: "famiglia", t: "Family", d: "Safe beaches, real space, games at home: happy kids, relaxed parents." },
        { id: "natura", t: "Nature", d: "Olive and carob groves, dry-stone walls and seaside nature reserves." },
        { id: "relax", t: "Relax", d: "The balcony, a book, the sun: the simplest holiday there is." },
        { id: "cultura", t: "Culture", d: "Ceramics, village festivals, museums and Montalbano locations." },
        { id: "escursioni", t: "Excursions", d: "Coastal trekking, horseback rides and boat days along the coast." },
      ],
    },
    audience: {
      eyebrow: "Perfect For",
      title: "Find yourself in your holiday",
      items: [
        { id: "coppie", t: "Couples", d: "For a romantic escape in Sicily, between seaside sunsets and dinners in the villages." },
        { id: "famiglie", t: "Families", d: "The space and freedom of feeling at home: games, a kitchen and safe beaches." },
        { id: "gruppi", t: "Groups of friends", d: "Three bedrooms and all the space to live the holiday together." },
        { id: "viaggiatori", t: "Travellers", d: "The ideal base to explore south-eastern Sicily, a new direction every day." },
      ],
    },
    reviews: {
      eyebrow: "Reviews",
      title: "In our guests' words",
      scoreWord: "Exceptional",
      countText: "Based on 10 verified reviews",
      verified: "Verified guest",
      reviewsWord: "reviews",
      mapsLink: "See all on Google Maps",
      readAll: "Read all reviews on Booking.com",
      cats: { cleanliness: "Cleanliness", comfort: "Comfort", value: "Value for money", staff: "Staff", facilities: "Facilities", location: "Location" },
      cta: "All reviews",
    },
    direct: {
      eyebrow: "Book Direct",
      title: "When you book directly with us",
      items: [
        { icon: "MessagesSquare", t: "Direct assistance", d: "You talk to the people who will welcome you, with no intermediaries." },
        { icon: "Smartphone", t: "Simple communication", d: "One WhatsApp message and you have the answer you're looking for." },
        { icon: "MapPinned", t: "Personalised tips", d: "Beaches, restaurants and villages: we'll tell you the ones we love." },
        { icon: "CalendarCheck", t: "Maximum flexibility", d: "Dates, arrival times, special needs: let's talk it over together." },
      ],
      cta: "Request a quote",
    },
    finalCta: {
      title: "Sicily is waiting for you.",
      text: "Bring your desire to discover. Casa Vacanze Margherita will take care of the rest.",
      cta: "Check availability",
    },
    footer: {
      tagline: "Sicily is waiting for you.",
      explore: "Explore",
      contacts: "Contact",
      rights: "All rights reserved.",
      payoff: "From Sea to Culture",
    },
    form: {
      title: "Check availability",
      subtitle: "Fill in the form: we'll get back to you as soon as possible with availability and a quote.",
      name: "Full name",
      email: "Email",
      phone: "Phone (optional)",
      checkin: "Check-in",
      checkout: "Check-out",
      guests: "Guests",
      pets: "Travelling with pets",
      message: "Message",
      messagePh: "Tell us something about your trip...",
      submit: "Send request",
      sending: "Sending...",
      success: "Request sent! We'll get back to you shortly.",
      error: "Something went wrong. Please try again or message us on WhatsApp.",
      whatsapp: "Continue on WhatsApp",
      waMsg: "Hello, I would like some information about Casa Vacanze Margherita for a stay",
    },
    contact: {
      title: "Contact",
      heroTitle: "Need some information?",
      heroSub: "Write to us directly: we personally reply to every message.",
      whatsapp: "WhatsApp",
      phone: "Phone",
      email: "Email",
      where: "Where we are",
      formTitle: "Write to us",
      send: "Send message",
      success: "Message sent! We'll reply soon.",
    },
    pages: {
      casa: { eyebrow: "The House", title: "A real Sicilian home, ready to welcome you", seo: "The House | Casa Vacanze Margherita", desc: "Discover the 110 sqm apartment: 3 bedrooms, equipped kitchen, living room with Smart TV, 3 balconies and every comfort for your Sicily holiday." },
      camere: { eyebrow: "The Rooms", title: "Real rooms, for real rest", seo: "Rooms | Casa Vacanze Margherita", desc: "Three bright bedrooms sleeping up to 6 guests: double, single and bunk bed room. Holiday home in Santa Croce Camerina, Ragusa." },
      gallery: { eyebrow: "Gallery", title: "See the house, imagine the holiday", seo: "Gallery | Casa Vacanze Margherita", desc: "Real photographs of Casa Vacanze Margherita: rooms, kitchen, spaces, details and the South-Eastern Sicily area." },
      dove: { eyebrow: "Location", title: "Between the sea and the Sicily you want to discover.", seo: "Location | Casa Vacanze Margherita — Santa Croce Camerina", desc: "Casa Vacanze Margherita is in Santa Croce Camerina (RG): minutes from Marina di Ragusa and Punta Secca, ideal base for Scicli, Modica, Ragusa Ibla and Noto." },
      cosa: { eyebrow: "What to See", title: "South-Eastern Sicily, day by day", seo: "What to See | South-East Sicily | Casa Vacanze Margherita", desc: "Marina di Ragusa, Punta Secca, Scicli, Modica, Ragusa Ibla, Noto: what to see near Casa Vacanze Margherita, with driving times." },
      esperienze: { eyebrow: "Experiences", title: "Don't just stay. Live Sicily.", seo: "Sicily Experiences | Casa Vacanze Margherita", desc: "Sea, baroque, food and wine, villages and nature: the experiences to live during your stay at Casa Vacanze Margherita." },
      recensioni: { eyebrow: "Reviews", title: "In our guests' words", seo: "Reviews | Casa Vacanze Margherita", desc: "What guests say about Casa Vacanze Margherita, holiday home in Santa Croce Camerina near Marina di Ragusa." },
      prenota: { eyebrow: "Book", title: "Book your stay", seo: "Book | Casa Vacanze Margherita", desc: "Check availability at Casa Vacanze Margherita and book your Sicily holiday directly." },
    },
    casaPage: {
      p1: "On the second floor of a quiet building in the heart of Santa Croce Camerina, Casa Vacanze Margherita is a 110 sqm apartment designed for those looking for a real home, not a hotel room.",
      p2: "The style is modern with vintage touches and Sicilian details: the lemon tablecloth, decorated tiles, a hand-drawn mandala. Every corner tells of the care of those who love this house.",
      petsTitle: "Your four-legged friends too",
      petsText: "Pets are always welcome: bring them along, they'll find space and cuddles here.",
      giftTitle: "Welcome home",
      giftText: "On arrival you'll find a small handcrafted gift that smells of Sicily: our way of welcoming you.",
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "How can I book?", a: "Directly from the website with the request form, or by messaging us on WhatsApp, email or phone. We'll reply with availability and a quote." },
        { q: "How many people does the house sleep?", a: "The apartment comfortably sleeps up to 6 people: perfect for families, couples and groups of friends." },
        { q: "Are pets allowed?", a: "Yes, your four-legged friends are always welcome." },
        { q: "How does check-in work?", a: "With smart self check-in you arrive whenever you prefer, with no time constraints." },
      ],
    },
    dovePage: {
      howTitle: "How to get here",
      car: { t: "By car", d: "From Catania take the A18 towards Ragusa and continue to Santa Croce Camerina (about 1h45). From Comiso Airport the house is about 30 minutes away." },
      plane: { t: "By plane", d: "The nearest airports are Comiso (CIY) and Catania (CTA). From the airport you can rent a car or book a private transfer." },
      bus: { t: "By bus", d: "Direct buses leave from Ragusa and Comiso to Santa Croce Camerina, with a stop a short walk from the house." },
      quiet: "The house is just a few metres from the historic centre, in a quiet, well-served area.",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("cvm-lang") || "it");
  useEffect(() => {
    localStorage.setItem("cvm-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
